/** Phase 3B-2a — Weekly Review Dashboard. */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, AlertTriangle, Trophy, TrendingDown, Info, Download } from "lucide-react";
import api from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { downloadCsv } from "@/lib/csv";
import CalibrationCoachCard from "@/components/CalibrationCoachCard";

function fmtPct(v, d = 2) { if (v == null) return "—"; return `${v > 0 ? "+" : ""}${v.toFixed(d)}%`; }
function toneOf(v) { return v == null || v === 0 ? "" : v > 0 ? "pnl-up" : "pnl-down"; }

function TradeCard({ trade, testId, showAlpha = true }) {
  const nav = useNavigate();
  return (
    <button
      onClick={() => trade.position_id && nav(`/replay/${trade.position_id}`)}
      className="w-full text-left border border-border/40 p-2 hover:bg-secondary/25 transition-colors focus:outline-none focus:ring-1 focus:ring-current"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <div className="mono text-sm">{trade.symbol}</div>
        <div className="text-[10px] mono uppercase text-muted-foreground">{trade.strategy}</div>
        <div className="ml-auto flex items-center gap-2">
          <span className={`mono text-xs ${toneOf(trade.net_return_pct)}`}>{fmtPct(trade.net_return_pct)}</span>
          {showAlpha && trade.relative_alpha_pct != null && (
            <span className={`mono text-[10px] uppercase ${toneOf(trade.relative_alpha_pct)}`}>α {fmtPct(trade.relative_alpha_pct)}</span>
          )}
        </div>
      </div>
    </button>
  );
}

function CardList({ title, trades, emptyMsg, testId, tone = "" }) {
  return (
    <div className="grid-cell !p-3" data-testid={testId}>
      <div className={`label-cap mb-2 ${tone}`}>{title}</div>
      {trades.length === 0
        ? <div className="text-xs text-muted-foreground py-3 text-center">{emptyMsg}</div>
        : <div className="space-y-1.5">{trades.map((t, i) => <TradeCard key={t.position_id || i} trade={t} testId={`${testId}-item-${i}`}/>)}</div>}
    </div>
  );
}

export default function WeeklyReviewSection({ filterParams }) {
  const [offset, setOffset] = useState(-1);
  const { data, isLoading } = useQuery({
    queryKey: qk(["performance", "weekly-review", offset], { filters: filterParams }),
    queryFn: async () => (await api.get("/performance/weekly-review", { params: { ...Object.fromEntries(Object.entries(filterParams).filter(([_, v]) => v != null)), week_offset: offset } })).data,
    staleTime: STALE.SHORT, gcTime: GC.SHORT,
  });

  // If the default (last completed) week is empty but earlier weeks had
  // activity, silently walk back until we find a non-empty week or run out.
  const [autoSearched, setAutoSearched] = useState(false);
  if (!autoSearched && data && data.trade_count === 0 && offset > -12) {
    setAutoSearched(true);
    setOffset(0);  // fall back to current week which likely has today's demo trades
  }

  if (isLoading || !data) return <div className="grid-cell text-xs text-muted-foreground" data-testid="weekly-loading">Loading weekly review…</div>;

  const exportCsv = () => {
    downloadCsv("weekly-review.csv", [
      ...(data.biggest_alpha_winners || []).map((r) => ({ section: "biggest_alpha_winners", ...r })),
      ...(data.biggest_alpha_losers || []).map((r) => ({ section: "biggest_alpha_losers", ...r })),
      ...(data.largest_ai_mistakes || []).map((r) => ({ section: "largest_ai_mistakes", ...r })),
      ...(data.biggest_user_improvements || []).map((r) => ({ section: "biggest_user_improvements", ...r })),
      ...(data.biggest_user_mistakes || []).map((r) => ({ section: "biggest_user_mistakes", ...r })),
    ], { context: { week_start: data.week_start, week_end: data.week_end } });
  };

  return (
    <section className="space-y-3" data-testid="perf-weekly-section" aria-labelledby="perf-weekly-title">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setOffset((o) => Math.max(o - 1, -52))} className="btn-ghost h-7 !px-2" data-testid="weekly-prev" aria-label="Previous week"><ChevronLeft size={14}/></button>
        <h2 id="perf-weekly-title" className="label-cap">
          Weekly Review · <span className="mono lowercase">{data.week_start}</span> — <span className="mono lowercase">{data.week_end}</span>
        </h2>
        <button onClick={() => setOffset((o) => Math.min(o + 1, 0))} className="btn-ghost h-7 !px-2" data-testid="weekly-next" aria-label="Next week" disabled={offset === 0}><ChevronRight size={14}/></button>
        <span className="text-[10px] mono uppercase text-muted-foreground">{offset === 0 ? "This week (in progress)" : offset === -1 ? "Last completed week" : `${-offset} weeks ago`}</span>
        <button onClick={exportCsv} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground ml-auto focus:outline-none focus:ring-1 focus:ring-current" data-testid="weekly-export">
          <Download size={10} className="inline mr-1"/>CSV
        </button>
      </div>

      {data.insufficient_data && (
        <div className="grid-cell border-signal-yellow/40 bg-signal-yellow/5 text-xs flex items-center gap-2" role="status" data-testid="weekly-empty">
          <AlertTriangle size={14} className="text-signal-yellow"/>No trades closed during this window. Try a previous week.
        </div>
      )}
      {data.small_sample_warning && !data.insufficient_data && (
        <div className="grid-cell border-signal-yellow/40 bg-signal-yellow/5 text-[11px] flex items-center gap-2" role="status" data-testid="weekly-small-sample">
          <AlertTriangle size={12} className="text-signal-yellow"/>Small sample — {data.trade_count} trades. Weekly rankings are directional at best.
        </div>
      )}

      <div className="grid-cell !p-3" data-testid="weekly-summary">
        <div className="label-cap mb-1">This week</div>
        <ul className="text-xs mono space-y-0.5 text-muted-foreground">
          {data.summary_lines.map((l, i) => <li key={i}>• {l}</li>)}
        </ul>
      </div>

      <CalibrationCoachCard compact={true} testId="weekly-calibration-coach"/>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
        <CardList title="Biggest Alpha Winners" trades={data.biggest_alpha_winners} emptyMsg="No positive-alpha trades yet." testId="weekly-alpha-winners"/>
        <CardList title="Biggest Alpha Losers" trades={data.biggest_alpha_losers} emptyMsg="No negative-alpha trades yet." testId="weekly-alpha-losers"/>
        <CardList title="Largest AI Mistakes" trades={data.largest_ai_mistakes} emptyMsg="No high-confidence losers this week." testId="weekly-ai-mistakes"/>
        <CardList title="Biggest User Improvements" trades={data.biggest_user_improvements} emptyMsg="No user overrides with positive alpha this week." testId="weekly-user-improvements"/>
        <CardList title="Overrides With Negative Outcome Impact" trades={data.biggest_user_mistakes} emptyMsg="No overrides with negative impact this week." testId="weekly-user-mistakes"/>
        <div className="grid-cell !p-3" data-testid="weekly-strategy-regime">
          <div className="label-cap mb-2">Best/Worst Strategy & Regime</div>
          <div className="space-y-2 text-xs">
            {data.best_strategy && (
              <div className="border border-border/40 p-2">
                <div className="flex items-center gap-2"><Trophy size={12} className="pnl-up"/><span className="mono">Best strategy · {data.best_strategy.strategy}</span></div>
                <div className="mono text-[10px] text-muted-foreground mt-0.5">n={data.best_strategy.trade_count} · PF {data.best_strategy.profit_factor.toFixed(2)} · sample: {data.best_strategy.sample_maturity}</div>
              </div>
            )}
            {data.worst_strategy && data.worst_strategy.strategy !== data.best_strategy?.strategy && (
              <div className="border border-border/40 p-2">
                <div className="flex items-center gap-2"><TrendingDown size={12} className="pnl-down"/><span className="mono">Worst strategy · {data.worst_strategy.strategy}</span></div>
                <div className="mono text-[10px] text-muted-foreground mt-0.5">n={data.worst_strategy.trade_count} · PF {data.worst_strategy.profit_factor.toFixed(2)}</div>
              </div>
            )}
            {data.best_regime && (
              <div className="border border-border/40 p-2">
                <div className="mono text-xs">Best regime · <span className="uppercase">{data.best_regime.regime}</span></div>
                <div className="mono text-[10px] text-muted-foreground">n={data.best_regime.trade_count} · P&L {fmtPct(data.best_regime.avg_return_pct)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
